/**
 * VÉRAULT Portal — Content Studio · Journal → Social Repurposing modal.
 *
 * Triggered from the Journal list (per-row action). Lets the advisor pick
 * target platforms, generates platform-tailored captions via AI, allows
 * inline editing, then saves selected captions as Social drafts.
 *
 * Backed by:
 *   POST /api/cms/ai/repurpose-journal  → { captions: {platform: text}, source_*}
 *   POST /api/cms/social                → creates one draft per kept caption
 */
import React from "react";
import {
  X,
  Sparkles,
  Instagram,
  Linkedin,
  Mail,
  Twitter,
  Facebook,
  Hash,
  RefreshCcw,
  Layers,
} from "lucide-react";
import { PortalButton, EditorialRule, api } from "../../../index";

const PLATFORMS = [
  { id: "instagram", label: "Instagram", icon: Instagram, limit: 2200 },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin, limit: 3000 },
  { id: "email", label: "Email", icon: Mail, limit: 1200 },
  { id: "facebook", label: "Facebook", icon: Facebook, limit: 2200 },
  { id: "twitter", label: "X", icon: Twitter, limit: 280 },
  { id: "threads", label: "Threads", icon: Hash, limit: 500 },
];

const TONES = [
  { id: "editorial", label: "Editorial" },
  { id: "calm", label: "Calm" },
  { id: "confident", label: "Confident" },
  { id: "warm", label: "Warm" },
  { id: "curious", label: "Curious" },
  { id: "playful", label: "Playful", secondary: true },
  { id: "intimate", label: "Intimate", secondary: true },
  { id: "sharp", label: "Sharp", secondary: true },
];

export function RepurposeModal({ open, entry, onClose, onSaved }) {
  const [selected, setSelected] = React.useState(["instagram", "linkedin", "email"]);
  const [tones, setTones] = React.useState(["editorial", "calm"]);
  const [variations, setVariations] = React.useState(1);
  const [captions, setCaptions] = React.useState(null); // {platform: text|list} once generated
  const [generating, setGenerating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setSelected(["instagram", "linkedin", "email"]);
      setTones(["editorial", "calm"]);
      setVariations(1);
      setCaptions(null);
      setError(null);
    }
  }, [open, entry?.id]);

  if (!open || !entry) return null;

  const togglePlatform = (id) => {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const generate = async () => {
    if (!selected.length) {
      setError("Choose at least one platform.");
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const r = await api.post("/cms/ai/repurpose-journal", {
        journal_id: entry.id,
        platforms: selected,
        tones,
        variations,
      });
      setCaptions(r.data.captions || {});
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "AI repurpose failed.");
    } finally {
      setGenerating(false);
    }
  };

  const toggleTone = (id) => {
    setTones((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const updateCaption = (platform, text, idx = null) => {
    setCaptions((c) => {
      const next = { ...(c || {}) };
      const cur = next[platform];
      if (Array.isArray(cur) && idx !== null) {
        const arr = [...cur];
        arr[idx] = text;
        next[platform] = arr;
      } else {
        next[platform] = text;
      }
      return next;
    });
  };

  const saveSelected = async () => {
    if (!captions) return;
    // Flatten arrays into {platform, caption} pairs to save each as its own draft
    const drafts = [];
    for (const p of selected) {
      const v = captions[p];
      if (Array.isArray(v)) {
        v.forEach((t) => {
          if (t && t.trim()) drafts.push({ platform: p, caption: t.trim() });
        });
      } else if (v && v.trim()) {
        drafts.push({ platform: p, caption: v.trim() });
      }
    }
    if (!drafts.length) {
      setError("Nothing to save — generate or write at least one caption.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        drafts.map((d) =>
          api.post("/cms/social", {
            caption: d.caption,
            platforms: [d.platform],
            destination_tag: entry.destination_tag || "",
            category: entry.category || "",
            source_journal_id: entry.id,
            source_journal_title: entry.title || "",
          }),
        ),
      );
      onSaved?.(drafts.length);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="repurpose-modal"
    >
      <div
        className="w-full max-w-2xl bg-[hsl(var(--background))] rounded-[var(--radius)] shadow-2xl border border-[rgba(0,0,0,0.08)] my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(0,0,0,0.06)]">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Repurpose for social
            </p>
            <p className="mt-0.5 font-serif text-lg truncate max-w-[28rem]" title={entry.title}>
              {entry.title || "Untitled"}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="repurpose-close"
            className="size-9 inline-flex items-center justify-center rounded-md text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              Target platforms
            </label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {PLATFORMS.map(({ id, label, icon: Icon }) => {
                const active = selected.includes(id);
                return (
                  <button
                    key={id}
                    onClick={() => togglePlatform(id)}
                    data-testid={`repurpose-platform-${id}`}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full font-body text-[12.5px] tracking-tight border transition-colors duration-200 ${
                      active
                        ? "bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))] border-transparent"
                        : "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-secondary))] border-[rgba(0,0,0,0.08)] hover:text-[hsl(var(--foreground))]"
                    }`}
                  >
                    <Icon className="size-3" strokeWidth={1.5} />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              Tone <span className="opacity-60 normal-case tracking-normal">(blend up to 3)</span>
            </label>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="repurpose-tones">
              {TONES.map(({ id, label, secondary }) => {
                const active = tones.includes(id);
                return (
                  <button
                    key={id}
                    onClick={() => toggleTone(id)}
                    disabled={!active && tones.length >= 3}
                    data-testid={`repurpose-tone-${id}`}
                    className={`inline-flex items-center h-7 px-2.5 rounded-full font-body text-[12px] tracking-tight border transition-colors duration-200 ${
                      active
                        ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] border-[hsl(var(--accent-gold))]/30"
                        : secondary
                        ? "bg-transparent text-[hsl(var(--foreground-muted))]/70 border-[rgba(0,0,0,0.06)] hover:text-[hsl(var(--foreground))] disabled:opacity-40 disabled:cursor-not-allowed"
                        : "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-secondary))] border-[rgba(0,0,0,0.08)] hover:text-[hsl(var(--foreground))] disabled:opacity-40 disabled:cursor-not-allowed"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] inline-flex items-center gap-2">
              <Layers className="size-3" strokeWidth={1.5} /> Variations per platform
            </label>
            <div className="inline-flex items-center gap-1" data-testid="repurpose-variations">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  onClick={() => setVariations(n)}
                  data-testid={`repurpose-variations-${n}`}
                  className={`size-8 inline-flex items-center justify-center rounded-full font-mono text-[11px] tabular-nums transition-colors ${
                    variations === n
                      ? "bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))]"
                      : "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {!captions ? (
            <div className="text-center py-6">
              <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-secondary))] max-w-md mx-auto">
                {generating
                  ? "Composing platform-tailored captions in the VÉRAULT voice…"
                  : "Generate platform-tailored captions in the VÉRAULT voice — then edit and save what you love as drafts."}
              </p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="repurpose-captions">
              <EditorialRule label="Captions" />
              {selected.map((p) => {
                const meta = PLATFORMS.find((x) => x.id === p);
                if (!meta) return null;
                const Icon = meta.icon;
                const value = captions[p];
                const items = Array.isArray(value) ? value : [value || ""];
                return (
                  <div key={p} className="space-y-2" data-testid={`repurpose-caption-${p}`}>
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-secondary))]">
                        <Icon className="size-3" strokeWidth={1.5} /> {meta.label}
                        {items.length > 1 && (
                          <span className="text-[hsl(var(--foreground-muted))] normal-case tracking-normal">
                            · {items.length} variations
                          </span>
                        )}
                      </span>
                    </div>
                    {items.map((text, idx) => {
                      const over = (text || "").length > meta.limit;
                      const isArr = Array.isArray(value);
                      return (
                        <div key={idx} className="space-y-1">
                          {isArr && (
                            <div className="flex items-center justify-between">
                              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">
                                Variation {idx + 1}
                              </span>
                              <span
                                className={`font-mono text-[10px] ${
                                  over ? "text-[hsl(var(--accent-terracotta))]" : "text-[hsl(var(--foreground-muted))]"
                                }`}
                              >
                                {(text || "").length} / {meta.limit}
                              </span>
                            </div>
                          )}
                          {!isArr && (
                            <div className="flex items-center justify-end">
                              <span
                                className={`font-mono text-[10px] ${
                                  over ? "text-[hsl(var(--accent-terracotta))]" : "text-[hsl(var(--foreground-muted))]"
                                }`}
                              >
                                {(text || "").length} / {meta.limit}
                              </span>
                            </div>
                          )}
                          <textarea
                            value={text || ""}
                            onChange={(e) => updateCaption(p, e.target.value, isArr ? idx : null)}
                            rows={Math.max(3, Math.min(8, Math.ceil(((text || "").length || 1) / 80)))}
                            data-testid={isArr ? `repurpose-textarea-${p}-${idx}` : `repurpose-textarea-${p}`}
                            className="w-full px-3 py-2.5 text-[14px] bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.08)] rounded focus:outline-none focus:border-[hsl(var(--accent-gold))] font-body leading-relaxed"
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {error && (
            <p
              className="font-body text-[13px] text-[hsl(var(--accent-terracotta))]"
              data-testid="repurpose-error"
            >
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[rgba(0,0,0,0.06)]">
          <PortalButton variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </PortalButton>
          {!captions ? (
            <PortalButton
              variant="primary"
              size="sm"
              icon={<Sparkles className="size-3.5" strokeWidth={1.5} />}
              onClick={generate}
              disabled={generating || !selected.length}
              data-testid="repurpose-generate"
            >
              {generating ? "Generating…" : "Generate captions"}
            </PortalButton>
          ) : (
            <>
              <PortalButton
                variant="ghost"
                size="sm"
                icon={<RefreshCcw className="size-3.5" strokeWidth={1.5} />}
                onClick={generate}
                disabled={generating}
                data-testid="repurpose-regenerate"
              >
                {generating ? "…" : "Regenerate"}
              </PortalButton>
              <PortalButton
                variant="primary"
                size="sm"
                onClick={saveSelected}
                disabled={saving}
                data-testid="repurpose-save"
              >
                {saving ? "Saving…" : `Save ${selected.length} draft${selected.length === 1 ? "" : "s"}`}
              </PortalButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default RepurposeModal;
